/*
 * @license
 * Copyright© Celligence International, LLC - 2023
 * 
 * This software program is protected by copyright laws and is the property of
 * Celligence International, LLC (Celligence). Any unauthorized reproduction,
 * distribution, modification, or public display of this program, without the
 * explicit permission of Celligence is strictly prohibited. If you have gained
 * access to this program without the expressed approval of Celligence report
 * the incident to legal@celligence.com.
 * 
 */
const axios = require('axios');

const SWMC_DOMAIN_NAME = "swmc";
const ANGELAI_DOMAIN_NAME = "angelai";
const ANGELLIFE_DOMAIN_NAME = "angellife";
const CELLIGENCE_DOMAIN_NAME = "celligence";
const EVENT_TYPE_ANGELAI_NFT = "AngelAiNFT";
const EVENT_TYPE_MINDSET_SUMMIT = "MindsetSummit";
const EVENT_TYPE_THIRD_PARTY = "ThirdParty";
const EVENT_SWMC_TYPE_THIRD_PARTY = "SWMCThirdParty";
const EVENT_SWMC_TYPE_THIRD_PARTY_ONE = "SWMCThirdParty";
const EVENT_ANGELLIFE_TYPE_THIRD_PARTY = "AngelLifeThirdParty";
const EVENT_ANGELAI_USER_EVENTS = "AngelAiNFT";
const IMGOING = "yes";
const MAYBE = "maybe"
const CANTGO = "no";
const VIP = "VIP";
const CDN_URL_RESOURCE = process.env.REACT_APP_CDN;
const EVENT_TEMPLATES_CDN_URL = CDN_URL_RESOURCE + "events-templates/";
const ANGELAI_USER_EVENT_CDN_URL = EVENT_TEMPLATES_CDN_URL + "assests/angelai-user-event/";
const DEFAULT_USER_THUMBNAIL = ANGELAI_USER_EVENT_CDN_URL + "default-user-thumbnail.webp";
const DEFAULT_CAROUSEL_THUMBNAIL = ANGELAI_USER_EVENT_CDN_URL + "default-carousel-thumbnail.webp";
const USER_ALLOWED_PARAM = "userAllowed";
const OTP_EXPIRATION_MINUTES = 15;
//As retry count is start from 0 then MAX_RETRY_ALLOWED value
//should be updated less 1 to match exact count. If we want 3 
//retry count max then MAX_RETRY_ALLOWED value should be updated as 2.
const MAX_RETRY_ALLOWED = 2;
const BLOCK_USER_IP = "BLOCK_USER_IP";
const BLOCK_USER_STATUS = "BLOCK_USER_STATUS";
const AUTHENTICATION_METHOD_EMAIL = "EMAIL";
const REACT_APP_MAXMIND = "https://0yfjxpmn64.execute-api.us-east-1.amazonaws.com/default/maxMind";
const IP_BLOCK_ERROR_MSG = "You have been temporarily locked out due to security reasons.";
// Number of hours after an event's scheduled date/time before its landing page
// automatically converts to the Post Event page.
const EVENT_EXPIRY_GRACE_PERIOD_HOURS = 24;
// Best-effort mapping of the timezone abbreviations stored on event data to a
// standard UTC offset (hours). A fixed 24 hour grace period is layered on top
// of the computed cutoff (see hasEventExpired), so the +/-1 hour slop between
// an abbreviation's standard/daylight variants (e.g. PST vs PDT) is immaterial.
const TIMEZONE_ABBREVIATION_UTC_OFFSETS = {
    UTC: 0, GMT: 0,
    EST: -5, EDT: -4, ET: -5,
    CST: -6, CDT: -5, CT: -6,
    MST: -7, MDT: -6, MT: -7,
    PST: -8, PDT: -7, PT: -8,
};

/**
 * @author Pratik Kajrolkar 
 * @date 06/06/2024
 * 
 * Determines whether the device accessing the page is a mobile
 *
 * @return {String} Returns "Mobile" if the device is a mobile
 */
function isMobileDevice() {
    const width = window.innerWidth;
    const isMobile = width < 992; // Adjust the values as needed

    if (isMobile) {
        return true;
    } else {
        return false;
    }
}

function isMobileNotTabletDevice() {
    const width = window.innerWidth;
    const isMobile = width < 768; // Adjust the values as needed

    if (isMobile) {
        return true;
    } else {
        return false;
    }
}

/**
 * Trims leading and trailing whitespace from a string.
 *
 * @param {string} theStr - The input string to be trimmed.
 * @return {string} The trimmed string.
 */
function trim(theStr) {
    return theStr.replace(/^\s\s*/, "").replace(/\s\s*$/, "");
}

/**
 * Extracts numbers from a given input string.
 *
 * @param {string} inVal - The input string from which numbers are to be extracted.
 * @param {boolean} isDouble - Indicates whether to allow decimal points.
 * @param {string} removeCharsFromPrefix - Characters to be removed from the beginning of the extracted numbers.
 * @return {string} The extracted numbers as a string.
 */
function getNumbers(inVal, isDouble, removeCharsFromPrefix) {
    var retVal = "";
    var okChars = "0123456789";
    var tmpVal = trim(inVal);
    var curChar = "";
    var isDotFound = false;

    if (tmpVal !== "") {
        for (var i = 0; i < tmpVal.length; i++) {
            curChar = tmpVal.charAt(i);

            if (okChars.indexOf(curChar) !== -1) {
                if (!isDotFound && curChar === "." && isDouble) {
                    isDotFound = true;
                    retVal = retVal + ".";
                }

                if (retVal === "" && removeCharsFromPrefix.indexOf(curChar) !== -1) {
                    //Do nothing
                } else {
                    retVal += curChar;
                }
            }
        }
    }

    return retVal;
}

/**
 * Serializes a form into an object with key-value pairs, where the keys are the form element IDs 
 * and the values are the form element values.
 *
 * @param {HTMLFormElement} form - The form to be serialized.
 * @return {Object} An object containing the serialized form data.
 */
function serialize(form) {
    let inputs = form.elements;
    let testArray = {};
    for (let i = 0; i < inputs.length - 1; i++) {
        testArray[inputs[i].id] = inputs[i].value;
    }
    return testArray;
}

/**
 * Returns the current form URL based on the current window location. 
 * If the window location has a third and fourth segment, it returns the URL 
 * with the third segment and the fourth segment without the hash symbol. 
 * If the window location does not have a third and fourth segment, it returns 
 * the root URL ("/").
 *
 * @return {string} The current form URL.
 */
function getFormURL() {
    if (
        window.location.href.split("/")[3] &&
        window.location.href.split("/")[4]
    ) {
        const temp = window.location.href.split("/")[4].replace("#", "");
        return "/" + window.location.href.split("/")[3] + "/" + temp;
    } else {
        return "/";
    }
}

/**
 * Removes non-ASCII special characters and replaces all special characters with an empty string.
 *
 * @param {string} str - The input string to be processed.
 * @return {string} The processed string with non-ASCII special characters and special characters removed.
 */
function removeNonAsciiSpecialCharacters(str) {
    //check if pass value is null or blank.
    //return empty string if pass value is null or blank
    if (str === null || str === "") {
        return "";
    } else {
        //convert the pass value to string
        str = str.toString();
        //remove non-printable ASCII chars and replace it with empty string
        str = str.replace(/[^\x20-\x7E]/g, "");
        //replace all special characters with empty string
        str = str.replace(/[`~!@#$%^&*()_|+\-=?;:'",.<>{}0-9\[\]\\\/]/g, "");
        //return updated string to calling function
        return str;
    }
}

/**
 * Removes non-ASCII characters and disallowed special characters for address fields.
 * Allows specific special characters like ',', '.', '#', '-', and '/'.
 *
 * @param {string} str - The input address string to be processed.
 * @return {string} The processed string with non-ASCII and disallowed special characters removed.
 */
function processAddressField(str) {
    // Check if the input value is null or blank.
    if (str === null || str === "") {
        return "";
    }

    // Convert the input value to a string.
    str = str.toString();

    // Remove non-printable ASCII characters.
    str = str.replace(/[^\x20-\x7E]/g, "");

    // Remove all disallowed special characters, keeping ',', '.', '#', '-', and '/'.
    const regex = /[^a-zA-Z0-9 ,.#-/]/g;
    str = str.replace(regex, "");

    return str;
}

/**
 * Removes non-ASCII characters and disallowed special characters for comment fields.
 * Allows specific special characters like '.', ',', '!', '?', '(', ')', "'", '"', and '\n'.
 *
 * @param {string} str - The input comment string to be processed.
 * @return {string} The processed string with non-ASCII and disallowed special characters removed.
 */
function processCommentField(str) {
    // Check if the input value is null or blank.
    if (str === null || str === "") {
        return "";
    }

    // Convert the input value to a string.
    str = str.toString();

    // Remove non-printable ASCII characters.
    str = str.replace(/[^\x20-\x7E]/g, "");

    // Remove all disallowed special characters, keeping '.', ',', '!', '?', '(', ')', "'", '"', and '\n'.
    const regex = /[^a-zA-Z0-9 .,!?()'"\n]/g;
    str = str.replace(regex, "");

    return str;
}

/**
 * Removes non-ASCII characters and disallowed special characters for city fields.
 * Allows specific special characters like '-' and '.'.
 *
 * @param {string} str - The input city string to be processed.
 * @return {string} The processed string with non-ASCII and disallowed special characters removed.
 */
function processCityField(str) {
    // Check if the input value is null or blank.
    if (str === null || str === "") {
        return "";
    }

    // Convert the input value to a string.
    str = str.toString();

    // Remove non-printable ASCII characters.
    str = str.replace(/[^\x20-\x7E]/g, "");

    // Remove all disallowed special characters, keeping '-' and '.'.
    const regex = /[^a-zA-Z0-9 .-]/g;
    str = str.replace(regex, "");

    return str;
}

/**
 * Extracts the YouTube video ID from a given URL, including Shorts.
 *
 * @param {string} url - The URL of the YouTube video.
 * @return {string|null} The extracted YouTube video ID, or null if no match is found.
 */
function getYouTubeVideoId(url) {
    const regex = /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:shorts\/|watch\?v=|embed\/|v\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/;
    const match = url.match(regex);
    return match ? match[1] : null;
}

/**
 * Resolves an event asset path from JSON. Full URLs are returned as-is, relative paths
 * (e.g. "naag2026/icon-01.svg") are resolved against the env specific events-templates CDN.
 *
 * @param {string} path - Full URL or path relative to events-templates/assests/
 * @return {string} The resolved asset URL, or empty string if no path is given.
 */
function getEventAssetUrl(path) {
    if (!path) return "";
    return /^https?:\/\//i.test(path) ? path : `${EVENT_TEMPLATES_CDN_URL}assests/${path}`;
}

/**
 * @author Pratik Kajrolkar
 * @date 06/05/2025
 *
 * Converts various date input formats into a formatted long-date string: "Month DD, YYYY"
 *
 * Supported input formats include:
 * - ISO format: "2025-07-07"
 * - US format: "07/07/2025"
 * - Long-form text: "July 7, 2025"
 * - Slash-separated: "2025/07/07"
 *
 * @param {string|Date} dateInput - The date string or Date object to be formatted.
 * @returns {string} - A formatted date string in the format "Month DD, YYYY" (e.g., "July 07, 2025").
 *                     Returns "Invalid date format" if the input cannot be parsed into a valid date.
 *
 * @example
 * formatDateToLong("2025-07-07");     // "July 07, 2025"
 * formatDateToLong("07/07/2025");     // "July 07, 2025"
 * formatDateToLong("July 7, 2025");   // "July 07, 2025"
 * formatDateToLong("2025/07/07");     // "July 07, 2025"
 * formatDateToLong(new Date());       // current date in "Month DD, YYYY"
 */
function formatDateToLong(dateInput) {
    const parsedDate = new Date(dateInput);

    // Check for invalid date
    if (isNaN(parsedDate.getTime())) {
        return null;
    }

    const options = { year: 'numeric', month: 'long', day: '2-digit' };
    return parsedDate.toLocaleDateString('en-US', options);
}

/**
 * Builds a multi-line venue address from whichever fields are actually present,
 * instead of assuming every field (name, streetAddress, city, state, zipCode) is
 * populated. Missing/empty fields are skipped rather than rendering literally as
 * "undefined" or leaving stray punctuation behind (e.g. a lone comma when city
 * is missing) — venue data comes from an external workflow with no schema
 * enforcement here, so any subset of fields may be present.
 *
 * @param {Object} venue - venue details; any of name, streetAddress, city, state, zipCode
 * @returns {string} HTML string with <br> between the lines that have content, or "" if none do.
 *
 * @example
 * formatVenueAddress({ name: "Grand Hall", city: "Austin", state: "TX" });
 * // "Grand Hall<br>Austin, TX"
 * formatVenueAddress({ streetAddress: "123 Main St", zipCode: "78701" });
 * // "123 Main St<br>78701"
 */
function formatVenueAddress(venue) {
    if (!venue) {
        return "";
    }

    const cityState = [venue.city, venue.state].filter(Boolean).join(", ");
    const cityStateZip = [cityState, venue.zipCode].filter(Boolean).join(" ");

    return [venue.name, venue.streetAddress, cityStateZip]
        .filter(Boolean)
        .join("<br>");
}

/**
 * Parses an event's stored `date` ("MM/DD/YYYY"), `time` (e.g. "01:00 PM", or
 * a range such as "12 p.m. to 4 p.m.") and `timezone` abbreviation (e.g.
 * "PDT") into the timestamp (ms since epoch, UTC) it represents.
 *
 * When a time range is given, the later value is used. When no time can be
 * parsed, the end of the day is assumed so an event is never treated as
 * expired before its scheduled day has passed. When the timezone can't be
 * recognized, falls back to interpreting the date/time in the browser's
 * local timezone.
 *
 * @param {Object} eventPageData - Event data containing `date`, `time`, `timezone`.
 * @returns {number|null} Timestamp in ms, or null if `date` can't be parsed.
 */
function getEventDateTime(eventPageData) {
    const dateMatch = String(eventPageData?.date || "").match(/(\d{1,2})\/(\d{1,2})\/(\d{4})/);
    if (!dateMatch) {
        return null;
    }
    const [, month, day, year] = dateMatch.map(Number);

    let hour = 23;
    let minute = 59;
    const timeMatches = [...String(eventPageData?.time || "").matchAll(/(\d{1,2})(?::(\d{2}))?\s*([ap])\.?m\.?/gi)];
    if (timeMatches.length) {
        const [, h, m, meridiem] = timeMatches[timeMatches.length - 1];
        hour = Number(h) % 12;
        minute = m ? Number(m) : 0;
        if (meridiem.toLowerCase() === "p") {
            hour += 12;
        }
    }

    const tzMatch = String(eventPageData?.timezone || eventPageData?.time || "").match(/\b([A-Z]{2,3})\b/);
    const utcOffsetHours = tzMatch ? TIMEZONE_ABBREVIATION_UTC_OFFSETS[tzMatch[1]] : undefined;
    if (utcOffsetHours === undefined) {
        return new Date(year, month - 1, day, hour, minute).getTime();
    }
    return Date.UTC(year, month - 1, day, hour - utcOffsetHours, minute);
}

/**
 * Determines whether an event's landing page should automatically convert to
 * the Post Event page: `gracePeriodHours` (default 24) after the event's
 * scheduled date/time has elapsed. Used so events expire on their own without
 * relying on someone manually flipping `showPostEventPage`.
 *
 * @param {Object} eventPageData - Event data containing `date`, `time`, `timezone`.
 * @param {number} [gracePeriodHours=24] - Hours to wait after the event before it expires.
 * @returns {boolean} True once the event is past its expiry window.
 */
function hasEventExpired(eventPageData, gracePeriodHours = EVENT_EXPIRY_GRACE_PERIOD_HOURS) {
    const eventTimestamp = getEventDateTime(eventPageData);
    if (eventTimestamp === null) {
        return false;
    }
    return Date.now() > eventTimestamp + gracePeriodHours * 60 * 60 * 1000;
}

/**
 * Converts a string or number to US currency format with dollar sign
 * 
 * @param {string|number} value - The value to format (string or number)
 * @param {boolean} [includeDecimal=true] - Whether to include decimal places (default: true)
 * @returns {string} Formatted currency string (e.g., "$1,234.56" or "$1,234")
 * @throws {Error} When input cannot be converted to a valid number
 * 
 * @example
 * formatUSCurrency(1234.56)           // "$1,234.56"
 * formatUSCurrency(1234.56, true)     // "$1,234.56"
 * formatUSCurrency(1234.56, false)    // "$1,235"
 * formatUSCurrency("1234.56")         // "$1,234.56"
 * formatUSCurrency(1234)              // "$1,234.00"
 * formatUSCurrency(1234, false)       // "$1,234"
 * formatUSCurrency(-500.25)           // "-$500.25"
 * formatUSCurrency(-500.25, false)    // "-$500"
 */
function formatUSCurrency(value, includeDecimal = true) {
    // Convert to number if it's a string
    const num = typeof value === 'string' ? parseFloat(value) : value;

    // Check if the conversion resulted in a valid number
    if (isNaN(num)) {
        throw new Error('Invalid input: cannot convert to number');
    }

    // Format as US currency with or without decimal places
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: includeDecimal ? 2 : 0,
        maximumFractionDigits: includeDecimal ? 2 : 0
    }).format(num);
}

/**
 * Converts a given string to lowercase and replaces all spaces with underscores.
 *
 * This is useful for creating URL-friendly or variable-friendly identifiers
 * from human-readable text (e.g., converting "My Text Here" to "my_text_here").
 *
 * @param {string} text - The input string to be transformed.
 * @returns {string} The transformed string in lowercase with spaces replaced by underscores.
 *
 * @example
 * toSnakeCase("Hello World");
 * // returns "hello_world"
 *
 * @example
 * toSnakeCase("Convert THIS to_snake Case");
 * // returns "convert_this_to_snake_case"
 */
function toSnakeCase(text) {
    return text.toLowerCase().replace(/\s+/g, '_');
}

/**
 * Capitalizes the first letter of each word in a given string.
 *
 * @param {String} inputString - The string to capitalize.
 * @return {String} The modified string with the first letter of each word capitalized.
 */
function capitalizeFirstLetter(inputString) {
    if (!inputString || typeof inputString !== 'string') {
        return '';
    }

    // Convert the entire string to lowercase
    const lowercaseString = inputString.toLowerCase();

    // Split the string into words using spaces
    const words = lowercaseString.split(' ');

    // Capitalize the first letter of each word
    const capitalizedWords = words.map(word => {
        return word.charAt(0).toUpperCase() + word.slice(1);
    });

    // Concatenate the modified words to form the final string
    return capitalizedWords.join(' ');
}

/**
 * @author Pratik Kajrolkar 
 * @date 12/17/2023
 * 
 * The below function is used to get the user machine details (e.g: IP address, location)
 * @return Response from the endpoint
 */
const getUserLocationDetails = async () => {
    try {
        const userLocationResponse = await axios.post(REACT_APP_MAXMIND, {},
            {
                headers: { 'Content-Type': 'application/json' },
            },
            { timeout: 5000 }
        );

        if (userLocationResponse && userLocationResponse.data) {
            if (
                userLocationResponse.data !== 'IP Not Available' &&
                userLocationResponse.data !== 'Invalid Country'
            ) {
                return userLocationResponse.data;
            }
        }
    } catch (e) {
        console.error("Unable to get user location details: ", e);
    }

    return null;
}

/**
 * @author Pratik Kajrolkar 
 * @date 12/17/2023
 * 
 * Check the status of blacklisted user IP address
 * @param {String} ipAddress - the user IP address
 * @return Response from the endpoint
 */
const checkBlackListedUserStatus = async (ipAddress) => {
    try {
        const blackListedUserResponse = await axios.post(process.env.REACT_APP_AUTHENTICATE_BLACKLIST, {
            methodName: BLOCK_USER_STATUS,
            ipAddress: ipAddress
        });

        if (blackListedUserResponse && blackListedUserResponse.data) {
            return blackListedUserResponse.data.allowed;
        }
    } catch (e) {
        console.error("Unable to check the status of blacklisted user IP address: ", e);
    }

    return false;
}

/**
 * @author Pratik Kajrolkar 
 * @date 12/17/2023
 * 
 * Blacklist user IP address for certain time period
 * @param {String} ipAddress - the user IP address
 * @param {Integer} timeInMinutes - the time in minutes till user will be blacklisted
 * @return Response from the endpoint
 */
const blackListUser = async (ipAddress, timeInMinutes) => {
    try {
        const blackListedUserResponse = await axios.post(process.env.REACT_APP_AUTHENTICATE_BLACKLIST, {
            methodName: BLOCK_USER_IP,
            ipAddress: ipAddress,
            expirationTime: timeInMinutes || OTP_EXPIRATION_MINUTES,
        });

        if (blackListedUserResponse && blackListedUserResponse.data) {
            return blackListedUserResponse.data.blacklistedInformation;
        }
    } catch (e) {
        console.error("Unable to black list user IP address: ", e);
    }

    return null;
}

// Store field value in local storage
const setFieldInLocalStorage = (key, value) => {
    const item = {
        value: value
    };
    // console.log("item: ", JSON.stringify(item));
    localStorage.setItem(key, JSON.stringify(item));
};

// Get field value store in local storage
const getFieldFromLocalStorage = (key) => {
    const itemString = localStorage.getItem(key);
    // console.log("itemString: ", itemString);
    if (!itemString) {
        return null;
    }
    const item = JSON.parse(itemString);
    return item.value;
};

// Store field value in local storage for particular time period
const setFieldExpiryInLocalStorage = (key, value, ttl) => {
    const now = new Date();
    const expiry = now.getTime() + ttl * 1000; // Convert TTL to milliseconds
    const item = {
        value: value,
        expiry: expiry,
    };
    // console.log("item: ", JSON.stringify(item));
    localStorage.setItem(key, JSON.stringify(item));
};

// Get field value store in local storage
const getFieldExpiryFromLocalStorage = (key) => {
    const itemString = localStorage.getItem(key);
    // console.log("itemString: ", itemString);
    if (!itemString) {
        return null;
    }
    const item = JSON.parse(itemString);
    // console.log("item: ", JSON.stringify(item));
    const now = new Date().getTime();
    // if field expiry is completed then remove field from
    // local storage and return null
    // console.log("now: ", now);
    // console.log("item.expiry: ", item.expiry);
    // console.log("now > item.expiry: ", now > item.expiry);
    if (now > item.expiry) {
        localStorage.removeItem(key);
        return null;
    }
    return item.value;
};

function scrollToElement(elementId) {
    var element = document.getElementById(elementId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
}

function maskEmailAddress(email) {
    const parts = email.split('@');
    const username = parts[0];
    const domain = parts[1];
    // Number of visible characters
    const visibleChars = Math.max(username.length - 2, 0);
    const maskedPart = '*'.repeat(visibleChars);

    return maskedPart + username.substring(visibleChars) + '@' + domain;
}

/**
 * Converts a string to Title Case (capitalizes first letter of every word).
 * @param {string} str - The input string to convert.
 * @returns {string} - The title-cased string.
 */
function toTitleCase(str) {
  return str
    .toLowerCase() // make entire string lowercase first
    .trim() // remove leading/trailing spaces
    .split(/\s+/) // split by one or more spaces
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/**
 * @author Pratik Kajrolkar
 * @date 06/05/2024
 *
 * The below code will exposed the variable and functions written
 * under this javascript file to other javascript files when
 * this file is object is created under them.
 */
module.exports = {
    SWMC_DOMAIN_NAME,
    ANGELAI_DOMAIN_NAME,
    ANGELLIFE_DOMAIN_NAME,
    CELLIGENCE_DOMAIN_NAME,
    EVENT_TYPE_ANGELAI_NFT,
    EVENT_TYPE_MINDSET_SUMMIT,
    EVENT_TYPE_THIRD_PARTY,
    EVENT_SWMC_TYPE_THIRD_PARTY,
    EVENT_SWMC_TYPE_THIRD_PARTY_ONE,
    EVENT_ANGELLIFE_TYPE_THIRD_PARTY,
    EVENT_ANGELAI_USER_EVENTS,
    IMGOING,
    MAYBE,
    CANTGO,
    VIP,
    CDN_URL_RESOURCE,
    EVENT_TEMPLATES_CDN_URL,
    ANGELAI_USER_EVENT_CDN_URL,
    DEFAULT_USER_THUMBNAIL,
    DEFAULT_CAROUSEL_THUMBNAIL,
    AUTHENTICATION_METHOD_EMAIL,
    USER_ALLOWED_PARAM,
    MAX_RETRY_ALLOWED,
    IP_BLOCK_ERROR_MSG,
    OTP_EXPIRATION_MINUTES,
    REACT_APP_MAXMIND,
    EVENT_EXPIRY_GRACE_PERIOD_HOURS,
    hasEventExpired,
    isMobileDevice,
    isMobileNotTabletDevice,
    trim,
    getNumbers,
    serialize,
    capitalizeFirstLetter,
    getFormURL,
    removeNonAsciiSpecialCharacters,
    processAddressField,
    processCityField,
    processCommentField,
    getYouTubeVideoId,
    getEventAssetUrl,
    formatDateToLong,
    formatVenueAddress,
    formatUSCurrency,
    toSnakeCase,
    getUserLocationDetails,
    checkBlackListedUserStatus,
    blackListUser,
    setFieldInLocalStorage,
    getFieldFromLocalStorage,
    setFieldExpiryInLocalStorage,
    getFieldExpiryFromLocalStorage,
    scrollToElement,
    maskEmailAddress,
    toTitleCase
}
